import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { ActionCenterItemV2 } from '@cloudcost/shared-types/action-center-v2';

import { ActionCenterModule } from '@/utils/actionCenterDetails';

const STORAGE_KEY = 'action_center_selection_v2';
const SELECTION_TTL_MS = 1000 * 60 * 60 * 12; // 12 hours

export interface ActionCenterViewSelection {
  module: ActionCenterModule;
  itemId: string;
  item?: ActionCenterItemV2;
  actionType: ActionCenterItemV2['action_type'];
  storedAt: number;
  originPath?: string;
  targetPath?: string;
}

export interface ActionCenterViewTriggerPayload {
  module: ActionCenterModule;
  item: ActionCenterItemV2;
  originPath?: string;
  targetPath?: string;
}

interface ActionCenterViewContextValue {
  selection: ActionCenterViewSelection | null;
  triggerView: (payload: ActionCenterViewTriggerPayload) => void;
  clearSelection: () => void;
}

const ActionCenterViewContext = createContext<ActionCenterViewContextValue | null>(
  null,
);

function readStoredSelection(): ActionCenterViewSelection | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ActionCenterViewSelection;
    if (!parsed?.storedAt) return null;
    const isExpired = Date.now() - parsed.storedAt > SELECTION_TTL_MS;
    if (isExpired) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    localStorage.removeItem(STORAGE_KEY);
    return null;
  }
}

/**
 * Provides state for currently selected Action Center item so tabs can react
 * consistently without relying on URL parameters.
 */
export function ActionCenterViewProvider({ children }: { children: ReactNode }) {
  const [selection, setSelection] = useState<ActionCenterViewSelection | null>(
    () => {
      try {
        return readStoredSelection();
      } catch {
        return null;
      }
    },
  );
  const selectionRef = useRef(selection);
  selectionRef.current = selection;

  const persistSelection = useCallback((value: ActionCenterViewSelection | null) => {
    if (!value) {
      localStorage.removeItem(STORAGE_KEY);
      return;
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  }, []);

  const triggerView = useCallback(
    (payload: ActionCenterViewTriggerPayload) => {
      const nextSelection: ActionCenterViewSelection = {
        module: payload.module,
        itemId: String(payload.item.id),
        item: payload.item,
        actionType: payload.item.action_type,
        storedAt: Date.now(),
        originPath: payload.originPath,
        targetPath: payload.targetPath,
      };
      setSelection(nextSelection);
      persistSelection(nextSelection);
    },
    [persistSelection],
  );

  const clearSelection = useCallback(() => {
    selectionRef.current = null;
    setSelection(null);
    persistSelection(null);
  }, [persistSelection]);

  useEffect(() => {
    // Keep localStorage in sync when selection changes outside triggerView.
    persistSelection(selection);
  }, [persistSelection, selection]);

  const value = useMemo<ActionCenterViewContextValue>(
    () => ({
      selection,
      triggerView,
      clearSelection,
    }),
    [selection, triggerView, clearSelection],
  );

  return (
    <ActionCenterViewContext.Provider value={value}>
      {children}
    </ActionCenterViewContext.Provider>
  );
}

export function useActionCenterViewContext() {
  const context = useContext(ActionCenterViewContext);
  if (!context) {
    throw new Error(
      'useActionCenterViewContext must be used within ActionCenterViewProvider',
    );
  }
  return context;
}

